import { useId, type ComponentPropsWithRef, type ReactNode } from 'react';

type Common = { label: string; hint?: ReactNode | undefined; error?: string | null | undefined };

function useFieldAttributes({
  id: suppliedId,
  hint,
  error,
  'aria-describedby': describedBy,
  'aria-invalid': invalid,
}: Omit<Common, 'label'> & Pick<ComponentPropsWithRef<'input'>, 'id' | 'aria-describedby' | 'aria-invalid'>) {
  const generatedId = useId();
  const id = suppliedId ?? generatedId;
  return {
    id,
    'aria-invalid': error ? true : invalid,
    'aria-describedby':
      [describedBy, hint ? `${id}-hint` : null, error ? `${id}-error` : null].filter(Boolean).join(' ') ||
      undefined,
  };
}

/** Run after the submit handler's synchronous validation has rendered, including repeated refusals. */
export function focusInvalidAfterSubmit(form: HTMLElement) {
  requestAnimationFrame(() => {
    if (form.isConnected) form.querySelector<HTMLElement>('[aria-invalid="true"]:not(:disabled)')?.focus();
  });
}

function Wrap({ id, label, hint, error, children }: Common & { id: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="kicker">
        {label}
      </label>
      {children}
      {hint ? (
        <p id={`${id}-hint`} className="text-[13px] text-inkFaint">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={`${id}-error`} className="text-[13px] text-dangerText" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

export function Field({
  label,
  hint,
  error,
  className = '',
  ...rest
}: Common & ComponentPropsWithRef<'input'>) {
  const attributes = useFieldAttributes({ ...rest, hint, error });
  return (
    <Wrap id={attributes.id} label={label} hint={hint} error={error}>
      <input {...rest} {...attributes} className={`field-input ${className}`} />
    </Wrap>
  );
}

export function TextArea({
  label,
  hint,
  error,
  className = '',
  ...rest
}: Common & ComponentPropsWithRef<'textarea'>) {
  const attributes = useFieldAttributes({ ...rest, hint, error });
  return (
    <Wrap id={attributes.id} label={label} hint={hint} error={error}>
      <textarea {...rest} {...attributes} className={`field-input min-h-[7rem] resize-y ${className}`} />
    </Wrap>
  );
}

export function Select({
  label,
  hint,
  error,
  className = '',
  children,
  ...rest
}: Common & ComponentPropsWithRef<'select'>) {
  const attributes = useFieldAttributes({ ...rest, hint, error });
  return (
    <Wrap id={attributes.id} label={label} hint={hint} error={error}>
      <select {...rest} {...attributes} className={`field-input ${className}`}>
        {children}
      </select>
    </Wrap>
  );
}
